// src/lib/errorLogger.ts
// ---------------------------------------------------------------------------
// Centralized, low-overhead error logger for the global resilience layer.
//
// Captures every failure surfaced by safeFetch / safeInvoke (and any caller
// that wants to participate) into an in-memory ring buffer. The buffer is
// intentionally bounded so it can never leak memory on a long-lived tab.
//
// In dev (or when ?debug=1 is on the URL) the buffer is exposed at
//   window.__kcErrors      — read the most recent failures
//   window.__kcErrors.dump()   — pretty-print the buffer to the console
//   window.__kcErrors.clear()  — empty the buffer
//
// IMPORTANT: this module never throws, never blocks the UI, and never makes
// network calls. It is safe to import from anywhere — including code paths
// that are themselves recovering from a failure.
// ---------------------------------------------------------------------------

export interface KcErrorEntry {
  ts: string;
  endpoint: string;
  status: number | null;
  bodyPreview: string;
  userId: string | null;
  functionName: string | null;
  attempt: number;
  kind: 'fetch' | 'invoke' | 'rpc' | 'db' | 'other';
  message: string;
}

const MAX_ENTRIES = 200;
const buffer: KcErrorEntry[] = [];

function previewBody(raw: unknown, max = 240): string {
  try {
    if (raw == null) return '';
    const s = typeof raw === 'string' ? raw : JSON.stringify(raw);
    return s.length > max ? `${s.slice(0, max)}…` : s;
  } catch {
    return String(raw).slice(0, max);
  }
}

let cachedUserId: string | null = null;
/**
 * Lightweight setter so the resilience layer can stamp errors with the
 * current Supabase auth user without importing the AuthContext (which would
 * create a circular dep). AppLayout wires this on mount.
 */
export function setLoggerUserId(id: string | null) {
  cachedUserId = id;
}

export function logError(entry: Omit<KcErrorEntry, 'ts' | 'userId'> & { userId?: string | null }) {
  const full: KcErrorEntry = {
    ts: new Date().toISOString(),
    endpoint: entry.endpoint,
    status: entry.status ?? null,
    bodyPreview: previewBody(entry.bodyPreview),
    userId: entry.userId ?? cachedUserId,
    functionName: entry.functionName ?? null,
    attempt: entry.attempt ?? 1,
    kind: entry.kind,
    message: entry.message,
  };
  buffer.push(full);
  if (buffer.length > MAX_ENTRIES) buffer.splice(0, buffer.length - MAX_ENTRIES);
  // Keep dev console useful but not noisy.
  try {
    if (typeof window !== 'undefined') {
      const env = (import.meta as unknown as { env?: { DEV?: boolean } }).env;
      const debug =
        window.location.search.includes('debug=1') || env?.DEV === true;
      if (debug) {
        // eslint-disable-next-line no-console
        console.warn(
          `[kc:resilience] ${full.kind} ${full.endpoint} → ${full.status ?? 'ERR'} (${full.message})`,
          full,
        );
      }
    }
  } catch {
    /* ignore */
  }
}

export function getErrorBuffer(): readonly KcErrorEntry[] {
  return buffer.slice();
}

export function clearErrorBuffer() {
  buffer.length = 0;
}

// Expose under a stable, non-conflicting name. Idempotent.
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__kcErrors = {
    get list() {
      return buffer.slice();
    },
    dump() {
      // eslint-disable-next-line no-console
      console.table(buffer.slice(-50));
    },
    clear: clearErrorBuffer,
  };
}

